import React from 'react';
import { Link } from 'react-router-dom'

const StockManagementSystem = () => {
  return (
    <section id="stock-management" className="py-16 bg-gradient-to-br from-blue-600 to-indigo-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            UZA -(POS) STOCK MANAGEMENT SYSTEM
          </h2>
          <p className="text-blue-100 text-lg max-w-3xl mx-auto">
            At Infinity Tech Africa, we built the UZA Stock Management System (POS) to help businesses in Kenya and across Africa take the hassle out of inventory management.
          </p>
        </div>

        {/* Stock Management Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
            <div className="relative">
              <img 
                src="/assets/UZA.png" 
                alt="UZA Stock Management System" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                <span className="animate-pulse mr-2">🛒</span> POS & STOCK CONTROL
              </div>
            </div>
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                Centralised, Automated & Built for Growth 🚀
              </h3>
              <p className="text-blue-600 font-semibold mb-4 text-lg">
                The all-in-one Stock Management & POS System for businesses in Kenya, Tanzania, Uganda, and Ghana
              </p>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Whether you run a retail shop, grocery store, supermarket, or wholesale business, <strong>UZA</strong> helps you stay in control. From the shop floor to the stockroom, UZA automates inventory tracking, processes sales seamlessly with our integrated Point of Sale (POS), and provides real-time analytics to keep your business ahead.
              </p>

              {/* Why Choose UZA */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">WHY UZA STOCK MANAGEMENT AND POS SYSTEM?</h4>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2 text-lg">✅</span>
                    <span className="text-gray-700"><strong>Dual Power:</strong> UZA is both a <em>POS & Stock Management System</em> – sell, track, and update stock all in one.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2 text-lg">📦</span>
                    <span className="text-gray-700"><strong>Low-Stock Alerts:</strong> Get instant notifications so you never run out of best-selling items.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2 text-lg">💳</span>
                    <span className="text-gray-700"><strong>Seamless M-PESA Integration:</strong> Accept payments instantly & auto-reconcile transactions.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2 text-lg">📊</span>
                    <span className="text-gray-700"><strong>Smart Analytics:</strong> Monitor sales trends & forecast demand with AI-powered insights.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2 text-lg">🌍</span>
                    <span className="text-gray-700"><strong>Multi-Location Support:</strong> Manage multiple branches and warehouses under one system.</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-600 mr-2 text-lg">⚡</span>
                    <span className="text-gray-700"><strong>Built for Africa:</strong> Affordable, reliable, and tailored for SMEs & enterprises.</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">What the UZA POS and Stock Management System Does</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid md:grid-cols-2 gap-3 text-gray-700">
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">✔</span>
                      <span><strong>Real-Time Tracking</strong> – Monitor stock instantly across branches</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">✔</span>
                      <span><strong>Smart Reordering</strong> – Automatic alerts to prevent stockouts</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">✔</span>
                      <span><strong>Supplier & Purchase Management</strong> – Record vendors, deliveries & payments in one place</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">✔</span>
                      <span><strong>Sales Insights & Forecasting</strong> – Understand trends & customer demand</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">✔</span>
                      <span><strong>Error-Free Operations</strong> – Reduce mistakes & boost efficiency</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-blue-600 mr-2">✔</span>
                      <span><strong>Multi-Device Access</strong> – Manage inventory anytime via web or mobile</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-blue-600 font-semibold mb-6 text-center text-lg flex justify-center items-center">
                <span className="mr-2">With UZA, you don’t just manage stock — you run your business smarter & faster!</span>
                <span className="text-2xl">🚀</span>
              </p>

              <Link
                to="https://stock.infinitytechafrica.com/"  
                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <span>Optimize Your Stock Management Today</span>
                <svg 
                  className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Transform Your Inventory Management?
            </h3>
            <p className="text-blue-100 mb-6 text-lg">
              Join businesses across East Africa that are streamlining their operations with UZA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                Contact Us Today
              </Link>
              {/* <Link
                to="/contact"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                Contact Us Today
              </Link> */}
            </div>
            <p className="text-yellow-200 mt-6 text-sm">
              Powered by Infinity Tech Africa. Create. Transform.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StockManagementSystem;
